import {WriteButton} from '../../common/buttons/WriteActions';
import {
    Alert, Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    FormControl,
    MenuItem,
    Select, Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography
} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useParams} from "react-router";
import {useEffect, useState} from "react";
import {ArrowBackIosNew, LocalOfferOutlined} from "@mui/icons-material";
import {DTO_STATE, RECIPE_SIZE} from "@mybake/ui/common/dictionary";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {IRecipe} from "../../../models/Recipe/IRecipe";
import {recipeAPI} from "../../../services/RecipeServices";
import Paper from "@mui/material/Paper";
import {DeleteRecipeDialog} from "./DeleteRecipeDialog";
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {IFilling} from "../../../models/Recipe/Filling/IFilling";
import {getIngredientAmount, getIngredientsTotalWeight} from "../shared/ingredientAmount";
import {AmountValue} from "../shared/AmountValue";
import {useCurrency} from '../../../hooks/useCurrency';
import {getApiErrorKind, getApiErrorMessage, getApiTraceId} from '../../../common/apiErrors';
import TraceIdFeedback from '../../feedback/TraceIdFeedback';
import {recipeGuidForForm} from '../shared/recipeFormHelpers';

export const RecipeDetailCard = () => {
    const {formatCurrency} = useCurrency();
    const navigate = useNavigate();
    const {id} = useParams();
    const [recipeForm, setRecipeForm] = useState<IRecipe | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [serverToast, setServerToast] = useState('');
    const [traceId, setTraceId] = useState('');
    const {data: recipe, error, isLoading, isSuccess} = recipeAPI.useGetRecipeV2Query(id ?? '', {skip: !id})
    const [deleteRecipe] = recipeAPI.useDeleteRecipeV2Mutation();
    const [size, setSize] = useState("1.00");
    const [selectedFillingName, setSelectedFillingName] = useState("");
    const [selectedFilling, setSelectedFilling] = useState<IFilling | undefined>(undefined);
    const handleRecipeSize = (e: any, counter: any) => {
        setSize(e.target.value);
    };
    const {t} = useAppTranslation();
    const recipeItems = recipeForm?.parts?.flatMap(part => part.items ?? []) ?? [];
    const totalWeight = getIngredientsTotalWeight([...recipeItems, ...(selectedFilling?.items ?? [])], size);
    const handleDelete = () => {
        setDeleteDialogOpen(!deleteDialogOpen);
    }

    const handleDeleteFilling = async () => {
        if (!recipeForm) return;

        const updated = {
            ...recipeForm,
            state: DTO_STATE.DTO_DELETED,
        };

        setDeleteError('');
        try {
            await deleteRecipe(String(updated.id)).unwrap();
            setRecipeForm(updated);
            navigate("..", {relative: "path"});
        } catch (requestError) {
            if (getApiErrorKind(requestError) === 'server') {
                setServerToast(getApiErrorMessage(requestError, t));
                setTraceId(getApiTraceId(requestError));
            } else setDeleteError(getApiErrorMessage(requestError, t));
        }
    };

    const handleSelectedFilling = (e: any) => {
        setSelectedFillingName(e.target.value);
        const filling = recipeForm?.fillings.find(f => f.fillingName === e.target.value);
        setSelectedFilling(filling);
    }

    useEffect(() => {
        if (isSuccess && recipe) {
            setRecipeForm(recipeGuidForForm(recipe));
        }
    }, [isSuccess, recipe]);

    useEffect(() => {
        if (!error || getApiErrorKind(error) !== 'server') return;
        setServerToast(getApiErrorMessage(error, t));
        setTraceId(getApiTraceId(error));
    }, [error, t]);

    return (<Container maxWidth="xl" sx={{py: 4,}}>
        <Button variant="outlined" startIcon={<ArrowBackIosNew/>}
                onClick={() => navigate("..", {relative: "path"})}>
            {t('recipes:back_to_recipes')}
        </Button>
        {error && getApiErrorKind(error) !== 'server' && <Alert severity="error" sx={{mt: 2}}>{getApiErrorMessage(error, t)}</Alert>}
        <Stack spacing={1}>
            <Card>
                <CardContent>
                    <Stack spacing={1}>
                        <Stack direction="row"
                               spacing={2}
                               justifyContent="space-between"
                               alignItems="center">
                            <Box>
                                <Typography variant="h5">
                                    {recipeForm?.recipeName}
                                </Typography>
                                {recipeForm?.productTypeName && (
                                    <Chip
                                        icon={<LocalOfferOutlined/>}
                                        label={recipeForm.productTypeName}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            mt: 0.75,
                                            height: 28,
                                            borderRadius: 1.5,
                                            borderColor: 'primary.light',
                                            bgcolor: 'rgba(25, 118, 210, 0.07)',
                                            color: 'primary.dark',
                                            fontWeight: 600,
                                            '& .MuiChip-icon': {color: 'primary.main', fontSize: 17},
                                        }}
                                    />
                                )}
                            </Box>
                            <Box paddingRight={2}>
                                <Stack direction="row"
                                       spacing={2}
                                       justifyContent="space-between"
                                       alignItems="center">
                                    <Typography variant="subtitle1">
                                         {t('recipes:proportion')}
                                    </Typography>
                                    <FormControl
                                        variant="outlined"
                                        size="medium"
                                    >
                                        <Select
                                            labelId="recipe_size_label"
                                            id="create_recipe_select"
                                            value={size}
                                            onChange={handleRecipeSize}
                                        >
                                            {RECIPE_SIZE.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                            </Box>
                        </Stack>
                        <Divider/>

                        <Stack direction={{xs: "column", md: "row",}} spacing={1}>
                            <Box flex={1}>
                                <Typography variant="caption">
                                    {t('recipes:description')}
                                </Typography>

                                <Typography variant="body1" sx={{mt: 1,}}>
                                    {recipeForm?.recipeDescription || "---"}
                                </Typography>
                            </Box>

                            <Box minWidth={200}>
                                <Typography variant="caption">
                                    {t('finance:price')}
                                </Typography>

                                <Typography variant="h5" sx={{mt: 1,}}>
                                    {
                                        formatCurrency(Number((recipeForm?.price || 0) + Number((selectedFilling?.proportionInfos[0]?.cost || 0))) * Number(size))
                                    }{" "}
                                    $
                                </Typography>
                                <Typography variant="caption" sx={{display: 'block', mt: 1}}>{t('items:weight')}</Typography>
                                <Typography variant="h5" sx={{mt: 1}}>{totalWeight.toFixed(2)} {t('items:grams_short')}</Typography>
                            </Box>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {/* PARTS */}

            <Card>
                <CardContent>
                    <Stack spacing={1}>
                        <Typography variant="h5">
                            {t('recipes:parts')}
                        </Typography>

                        <Divider/>
                        {recipeForm?.parts?.map((part) => (
                            <Card>
                                {/* ITEMS */}

                                <CardContent>
                                    <Stack spacing={1}>
                                        <Typography variant="h4">
                                            {part.partName}
                                        </Typography>

                                        <Divider/>

                                        <TableContainer component={Paper} elevation={0}>
                                            <Table>
                                                <TableBody>
                                                    {part?.items?.map((item) => (
                                                            <TableRow key={item.id}>
                                                                <TableCell>
                                                                    {item.itemName}
                                                                </TableCell>

                                                                <TableCell align="right">
                                                                    <AmountValue quantity={getIngredientAmount(item, size).quantity} unit={item.unitOfWeight}/>
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                </CardContent>

                {recipeForm !== null && recipeForm?.fillings.length !== 0 && (<CardContent>
                        <Stack spacing={1}>
                            <Typography variant="h5">
                                {t('recipes:filling')}
                            </Typography>
                            <FormControl
                                variant="outlined"
                                size="medium"
                            >
                                <Select
                                    labelId="filling_name"
                                    id="filling_name"
                                    value={selectedFillingName}
                                    onChange={handleSelectedFilling}
                                >
                                    <MenuItem value="">
                                        {t('recipes:not_selected')}
                                    </MenuItem>
                                    {recipeForm?.fillings.map((option) => (
                                        <MenuItem key={option.fillingName} value={option.fillingName}>
                                            {option.fillingName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {selectedFilling !== undefined &&
                                <TableContainer component={Paper} elevation={0}>
                                    <Table>
                                        <TableBody>
                                            {selectedFilling.items.map((fi) => (
                                                    <TableRow key={fi.id}>
                                                        <TableCell>
                                                            {fi.itemName}
                                                        </TableCell>

                                                        <TableCell align="right">
                                                            <AmountValue quantity={getIngredientAmount(fi, size).quantity} unit={fi.unitOfWeight}/>
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            }
                        </Stack>
                    </CardContent>
                )}
            </Card>
            {/* ACTIONS */}

            <Stack direction="row" spacing={2} justifyContent="flex-end">
                <WriteButton variant="outlined" startIcon={<EditIcon/>}
                        onClick={() => navigate('edit')}
                >
                    {t('common:edit')}
                </WriteButton>

                <WriteButton
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon/>}
                    onClick={handleDelete}
                >
                    {t('common:delete')}
                </WriteButton>
            </Stack>
        </Stack>
        {deleteDialogOpen && <DeleteRecipeDialog open={deleteDialogOpen} onClose={handleDelete}
                                                 recipeName={recipeForm?.recipeName || ''}
                                                 onDelete={handleDeleteFilling} error={deleteError}/>}
        <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')}><Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert></Snackbar>
    </Container>);
}
